import React, { useMemo, useEffect } from 'react';
import { marked } from 'marked';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import DOMPurify from 'dompurify';

// TheoryRenderer
// ──────────────
// Что мы хотим:
//   • заголовки разной величины: # H1, ## H2, ### H3
//   • **bold**, _italic_, списки, инлайн-код, блоки кода
//   • формулы: $inline$ и $$block$$ через KaTeX
//   • при копировании из Word/Google Docs форматирование не «уезжало»
//
// Почему именно Markdown + KaTeX + DOMPurify:
//   • Markdown — текст. Что бы пользователь ни вставил, мы достанем чистый текст
//     (компонент-редактор форсит paste-as-plain). Никаких inline-стилей.
//   • marked — единственная зависимость на парсинг MD; уже даёт XSS-safe HTML
//     при дефолтных настройках, мы дополнительно прогоняем через DOMPurify.
//   • KaTeX рендерит формулы в HTML (без JS на странице студента).
//
// Контракт: на вход — Markdown-строка; на выход — отрендеренный JSX через
// dangerouslySetInnerHTML, но строка строго прошла через DOMPurify.

// Маркеры — видимые ASCII-токены с УНИКАЛЬНЫМ open+close.
// Раньше использовались control-bytes \x01-\x04 как разделители — но они
// стриппались DOMPurify (или marked в некоторых версиях) → плейсхолдеры
// ломались → формулы не рендерились или показывались как `MATHBLOCK0` текстом.
//
// Текущий формат: `xxKBLKxx_<idx>_xxKENDxx` — гарантированно не встретится
// в человеческом тексте, не зависит от sanitizer'ов, regex детерминированный
// благодаря явному close-токену (исключает digit-greedy collision).
const BLOCK_OPEN = 'xxKBLKxx_';
const BLOCK_CLOSE = '_xxKENDxx';
const INLINE_OPEN = 'xxKINLxx_';
const INLINE_CLOSE = '_xxKENDxx';

function renderKatex(src, displayMode) {
  try {
    return katex.renderToString(src, {
      displayMode,
      throwOnError: false,
      strict: 'ignore',
      trust: false,
      output: 'html',
    });
  } catch {
    // Если не парсится — отдаём исходный текст в <code>, не падаем.
    return `<code>${escapeHtml(src)}</code>`;
  }
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * 1) Вытаскиваем все $$...$$ и $...$ блоки, заменяем их плейсхолдерами.
 * 2) Прогоняем оставшийся текст через marked (Markdown → HTML).
 * 3) Подставляем плейсхолдеры обратно — уже отрендеренными KaTeX'ом.
 *
 * Порядок важен: сначала $$ (block), потом $ (inline), иначе block-формула
 * проглотит inline-парсер.
 */
function markdownWithMath(input) {
  const blocks = [];
  const inlines = [];

  // Block math: $$...$$  (на отдельной строке либо посреди текста)
  let s = String(input || '').replace(/\$\$([\s\S]+?)\$\$/g, (_, src) => {
    const idx = blocks.push(src.trim()) - 1;
    return `${BLOCK_OPEN}${idx}${BLOCK_CLOSE}`;
  });

  // Inline math: $...$ — но не «доллар суммы» $5 — требуем,
  // чтобы внутри был хоть один не-цифровой символ.
  s = s.replace(/(^|[^\\$])\$([^\n$]+?)\$/g, (_, pre, src) => {
    if (/^\s*$/.test(src) || /^\s*\d+(\.\d+)?\s*$/.test(src)) {
      return `${pre}$${src}$`;
    }
    const idx = inlines.push(src.trim()) - 1;
    return `${pre}${INLINE_OPEN}${idx}${INLINE_CLOSE}`;
  });

  // Markdown-парсинг — gfm + breaks (одиночный \n превращается в <br>).
  marked.setOptions({ gfm: true, breaks: true });
  let html = marked.parse(s);

  // Подставляем формулы обратно. Регекс строго ограничен open+close
  // токенами → digit-greedy collision невозможна.
  html = html.replace(
    new RegExp(`${BLOCK_OPEN}(\\d+)${BLOCK_CLOSE}`, 'g'),
    (_, i) => `<div class="theory-katex-block">${renderKatex(blocks[Number(i)], true)}</div>`
  );
  html = html.replace(
    new RegExp(`${INLINE_OPEN}(\\d+)${INLINE_CLOSE}`, 'g'),
    (_, i) => renderKatex(inlines[Number(i)], false)
  );

  return html;
}

const TheoryRenderer = ({ content, className = '' }) => {
  // Конфигурируем DOMPurify единожды: KaTeX генерит много span с inline-стилями,
  // поэтому стили нам нужны (без них формулы выглядят сломанными).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    DOMPurify.addHook?.('uponSanitizeAttribute', (node, hook) => {
      // Разрешаем style-атрибут только в KaTeX-контейнерах — обычные <p style>
      // из вставленного HTML мы хотим стерилизовать.
      if (hook.attrName === 'style' && !/katex/.test(node.className || '')) {
        hook.keepAttr = false;
      }
    });
  }, []);

  const safeHtml = useMemo(() => {
    if (!content) return '';
    const raw = markdownWithMath(content);
    return DOMPurify.sanitize(raw, {
      ADD_TAGS: ['math', 'mtext', 'mn', 'mo', 'mi', 'mrow', 'msup', 'msub', 'mfrac', 'msqrt'],
      ADD_ATTR: ['class', 'style', 'aria-hidden', 'role'],
    });
  }, [content]);

  return (
    <div
      className={`theory-prose max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};

export default TheoryRenderer;
